import { API } from '@/http-common.js';
import http_functions from '@/mixin/http_functions';
import {
    addSessionTypeLabel,
    addSessionTypeShortcut,
    buildDate,
    buildDocumentStatut,
    buildPifDocumentStatut,
    buildPifSignatureDocumentStatut,
    buildProduitTypeLabel,
    buildProduitTypeShortcut
} from '@/stores/utils';
import { Filters } from '@/types/gestion/inscriptions-filters-types';
import {
    Inscription,
    InscriptionResponse
} from '@/types/gestion/inscriptions-types';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { LocationQuery } from 'vue-router';

export const INSCRIPTIONS_ACTIVES = 'ACTIVES';
export const INSCRIPTIONS_ANNULEES_TRANSFEREES = 'ANNULEES_TRANSFEREES';

const noFilterState: Filters = {
    clientIds: [],
    formationId: undefined,
    date: undefined,
    datePeriodeId: undefined,
    datePeriodeDebut: undefined,
    datePeriodeFin: undefined,
    contactId: undefined,
    action: undefined,
    organisateurId: undefined,
    modeId: undefined,
    inscriptions: undefined
};

const filterInitialState: Filters = {
    clientIds: [],
    formationId: undefined,
    date: undefined,
    datePeriodeId: undefined,
    datePeriodeDebut: undefined,
    datePeriodeFin: undefined,
    contactId: undefined,
    action: undefined,
    organisateurId: undefined,
    modeId: undefined,
    inscriptions: INSCRIPTIONS_ACTIVES
};

export const INSCRIPTIONS_FILTERS_CONFIGURATION = {
    clientIds: {
        name: 'clientIds',
        isArray: true
    },
    formationId: {
        name: 'formationId',
        isArray: false
    },
    date: {
        name: 'date',
        isArray: false
    },
    datePeriodeId: {
        name: 'datePeriodeId',
        isArray: false
    },
    datePeriodeDebut: {
        name: 'datePeriodeDebut',
        isArray: false
    },
    datePeriodeFin: {
        name: 'datePeriodeFin',
        isArray: false
    },
    contactId: {
        name: 'contactId',
        isArray: false
    },
    action: {
        name: 'action',
        isArray: false
    },
    organisateurId: {
        name: 'organisateurId',
        isArray: false
    },
    modeId: {
        name: 'modeId',
        isArray: false
    },
    statutId: {
        name: 'statutId',
        isArray: false
    },
    inscriptions: {
        name: 'inscriptions',
        isArray: false
    }
};

export const useInscriptionFilterStore = defineStore(
    'inscriptions-filter',
    () => {
        const filters = ref<Filters>(filterInitialState);

        const setFilter = (name: string, value: any) => {
            filters.value = { ...filters.value, [name]: value };
        };
        const resetFilters = () => {
            filters.value = noFilterState;
        };

        const parsePayload = (payload: LocationQuery): Filters => {
            const newPayload = { ...payload };

            Object.keys(newPayload).forEach((key) => {
                const filter = INSCRIPTIONS_FILTERS_CONFIGURATION[key] as any;

                if (filter && filter.isArray) {
                    newPayload[key] = newPayload[key].split(',');
                }
                if (
                    [
                        INSCRIPTIONS_FILTERS_CONFIGURATION.date.name,
                        INSCRIPTIONS_FILTERS_CONFIGURATION.datePeriodeDebut
                            .name,
                        INSCRIPTIONS_FILTERS_CONFIGURATION.datePeriodeFin.name
                    ].includes(key)
                ) {
                    newPayload[key] = new Date(newPayload[key]);
                }
            });

            return newPayload;
        };

        const initFiltersFromQuery = (payload: LocationQuery) => {
            const parsedPayload = parsePayload(payload) as Filters;
            if (
                parsedPayload.inscriptions !== INSCRIPTIONS_ACTIVES &&
                parsedPayload.inscriptions !== INSCRIPTIONS_ANNULEES_TRANSFEREES
            ) {
                parsedPayload.inscriptions = INSCRIPTIONS_ACTIVES;
            }
            if (payload && Object.keys(payload).length > 0) {
                filters.value = {
                    ...noFilterState,
                    ...parsedPayload
                };
            } else {
                filters.value = {
                    ...filterInitialState,
                    ...parsedPayload
                };
            }
        };

        return {
            resetFilters,
            setFilter,
            filters,
            initFiltersFromQuery
        };
    }
);

export const useInscriptionStore = defineStore('inscriptions', () => {
    const inscriptions = ref<Inscription[]>([]);

    const exportCsv = async (query: Filters) => {
        http_functions.methods.downloadFile(
            `client/v2/inscriptions/exporter${query ? `?${new URLSearchParams(query).toString()}` : ''}`,
            'export.csv'
        );
    };

    const size = 100;

    const page = ref(0);
    const totalElement = ref(undefined);
    const totalPages = ref(0);

    const loadMore = async (query: Filters) => {
        page.value = page.value + 1;
        const body = {
            ...query,
            size,
            page: page.value
        };
        const response = await API.get(`client/v2/inscriptions/search`, {
            params: body
        });
        if (response?.data) {
            const inscriptionResponse = response?.data.content.map(
                (c: InscriptionResponse) => mapInscription(c)
            );
            inscriptions.value = [
                ...inscriptions.value,
                ...inscriptionResponse
            ];
        }
    };

    const fetchInscriptions = async (query: Filters) => {
        page.value = 0;
        const body = {
            ...query,
            size,
            page: 0
        };

        const response = await API.get(`client/v2/inscriptions/search`, {
            params: body
        });

        if (response?.data) {
            inscriptions.value = response?.data.content.map(
                (i: InscriptionResponse) => mapInscription(i)
            );
            totalElement.value = response?.data.totalElements;
            totalPages.value = response?.data.totalPages;
        }
    };

    const mapInscription = (i: InscriptionResponse): Inscription => {
        return {
            ...i,
            clientDenomination: i?.client?.denomination,
            clientSiret: i.client.siret ?? undefined,
            participantNomPrenom: i.nomPrenom,
            organisateur: i.organisateur,
            typeShortcut: addSessionTypeShortcut(
                buildProduitTypeShortcut(
                    i.produit.type.id,
                    i.produit.lienElearning
                ),
                i.sessionStatut?.id
            ),
            typeLabel: addSessionTypeLabel(
                buildProduitTypeLabel(
                    i.produit.type.label,
                    i.produit.lienElearning
                ),
                i.sessionStatut?.id
            ),
            modeLabel: i?.mode?.label,
            statutLabel:
                i?.statut?.id === -1
                    ? 'Partiellement confirmé'
                    : i.statut.label,
            sessionStatutLabel: i.sessionStatut?.label,
            ...(i.convention?.id && {
                conventionStatut: buildDocumentStatut(
                    i.convention?.disponible,
                    i.convention?.signee,
                    i.convention?.validee,
                    i.convention?.pending
                )
            }),
            ...(i.pif?.id && {
                pifStatut: buildPifSignatureDocumentStatut(
                    i.pif?.disponible,
                    i.pif?.signee,
                    i.pif?.validee,
                    i.pif?.pending,
                    i.pif?.pendingParticipant
                )
            }),
            ...(i.hasCertificats && {
                certificatLabel: 'Télécharger'
            }),
            ...(i.testPositionnement && {
                testPositionnementStatut: buildTestPositionnementStatut(
                    i.testPositionnement?.disponible,
                    i.testPositionnement?.realise
                )
            }),
            clientId: i.client.id,
            titre: `${i.produit.titre}${i.familleStageEC ? ' (Stage EC)' : ''}${i.familleStageCAC ? ' (Stage CAC)' : ''}`,
            datesFormations: buildDate(i.dateDebut, i.dateFin),
            disabled: i.sessionStatut?.id === 5
        };
    };

    const buildTestPositionnementStatut = (
        disponible?: boolean,
        realise?: boolean
    ) => {
        if (!disponible) {
            return 'Non requis';
        } else if (disponible && !realise) {
            return 'Non réalisé';
        } else if (realise) {
            return 'Réalisé';
        }
    };

    return {
        loadMore,
        totalElement,
        page,
        totalPages,
        inscriptions,
        fetchInscriptions,
        exportCsv
    };
});
